import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import {
  closePromotionModal,
  openSuccessPromotionAlertModal,
  setProfilePromotionItemId,
  setPromotionModalView,
  togglePromotionModalView,
  toggleRestoreAlertModal
} from '../actions';
import Bugsnag from '../utils/bugsnag';
import {
  selectProfilePromotionItemId,
  selectPromotionModalViewState
} from '../store/selectors';
import { payForItemMutation, prolongItemMutation } from '../api/mutations';
import { ItemStatusEnum } from '../graphql/graphql';
import { PROMOTION_VIEWS, PromotionView } from '../utils/constants/promotion';
import {
  useHistoryBackClose,
  waitForActiveHistoryOverlayClose
} from './useHistoryBackClose';

interface Item {
  actions?: string[];
}

interface UseTogglePromotionModalViewOptions {
  trackHistory?: boolean;
}

export const useTogglePromotionModalView = (
  item?: Item | undefined,
  setShowReloadPageAlert?: React.Dispatch<React.SetStateAction<boolean>>,
  options?: UseTogglePromotionModalViewOptions
) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { view: promotionView, isVisible } = useSelector(
    selectPromotionModalViewState
  );
  const isPremiumView = promotionView === PROMOTION_VIEWS.feature;
  const isRestoreView = promotionView === PROMOTION_VIEWS.restore;
  const isPaymentMethodView = promotionView === PROMOTION_VIEWS.limitPayment;
  const isPersonalAdsLimitView = promotionView === PROMOTION_VIEWS.personalAds;
  const { id } = router.query;
  const isPayForLimitByPersonalAdsAvailable = item?.actions?.includes('PAY');
  const isProlongByPersonalAdsPaymentAvailable =
    item?.actions?.includes('PROLONG');
  const { profileItemId } = useSelector(selectProfilePromotionItemId);

  const [prolongItem, { loading: prolongItemLoad }] =
    useMutation(prolongItemMutation);
  const [payForItem, { loading: payForItemLoad }] =
    useMutation(payForItemMutation);

  const onCloseHandler = () => {
    dispatch(closePromotionModal());
    dispatch(setProfilePromotionItemId(null));
  };

  const requestClose = useHistoryBackClose(
    options?.trackHistory ? isVisible : false,
    onCloseHandler
  );

  const openCorrectView = (route: PromotionView) => {
    dispatch(togglePromotionModalView(true));
    dispatch(setPromotionModalView(route));
  };

  const onToggleRestoreAlertModal = (isOpen: boolean) => {
    dispatch(toggleRestoreAlertModal(isOpen));
  };

  const onOpenRestoreViewHandler = () => {
    if (isProlongByPersonalAdsPaymentAvailable) {
      onToggleRestoreAlertModal(true);
      return;
    }
    openCorrectView(PROMOTION_VIEWS.restore);
  };

  const onOpenLimitPaymentViewHandler = () => {
    if (isPayForLimitByPersonalAdsAvailable) {
      openCorrectView(PROMOTION_VIEWS.personalAds);
      return;
    }
    openCorrectView(PROMOTION_VIEWS.limitPayment);
  };

  const onDeclineRestorePayment = () => {
    onToggleRestoreAlertModal(false);
  };

  const onAcceptRestorePaymentHandleClick = async () => {
    if (prolongItemLoad) return false;
    onToggleRestoreAlertModal(false);
    prolongItem({
      variables: {
        itemId: (id || profileItemId) as string
      }
    })
      .then(data => {
        if (data.data?.prolongItem?.item?.state === ItemStatusEnum.Published) {
          dispatch(openSuccessPromotionAlertModal(PROMOTION_VIEWS.restore));
        }
        if (data.data?.prolongItem?.errors.length && setShowReloadPageAlert) {
          setShowReloadPageAlert(true);
        }
      })
      .catch(error => {
        Bugsnag.notify(error);
      });
  };

  const onLimitPaymentHandleClick = () => {
    if (payForItemLoad) return false;
    payForItem({
      variables: {
        itemId: (id || profileItemId) as string
      }
    })
      .then(async data => {
        if (data.data?.payForItem?.item?.state === ItemStatusEnum.Pending) {
          onCloseHandler();
          dispatch(
            openSuccessPromotionAlertModal(PROMOTION_VIEWS.limitPayment)
          );
        }
        if (data.data?.payForItem?.item?.state === ItemStatusEnum.Published) {
          await waitForActiveHistoryOverlayClose();
          onCloseHandler();
          window.location.reload();
        }
        if (data.data?.payForItem?.errors.length && setShowReloadPageAlert) {
          setShowReloadPageAlert(true);
        }
      })
      .catch(error => {
        Bugsnag.notify(error);
      });
  };

  return {
    onCloseHandler,
    requestClose,
    openCorrectView,
    promotionView,
    isPremiumView,
    isPaymentMethodView,
    isRestoreView,
    isPersonalAdsLimitView,
    onOpenRestoreViewHandler,
    onOpenLimitPaymentViewHandler,
    onAcceptRestorePaymentHandleClick,
    onDeclineRestorePayment,
    onLimitPaymentHandleClick
  };
};
